import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DialogFooter } from "@/components/ui/dialog";
import { AlertCircle } from "lucide-react";
import { ConversionPreview } from "@/components/conversion-preview/ConversionPreview";
import { Account } from "@accountHooks";
import { useCurrencyList } from "@/hooks/useCurrencyList";
import { useExchangeRates } from "@/hooks/useExchangeRates";
import { getDisplayName, disambiguateDisplayNames } from "@/lib/display-name";
import {ReitHolding} from "@/@types/reit.models.ts";
import {CreateReitDepositDto} from "@/@types/reit.dto";

interface REITDepositFormProps {
  holdings: ReitHolding[];
  accounts: Account[];
  preselectedHoldingId?: string;
  onSave: (data: CreateReitDepositDto) => void | Promise<void>;
  onCancel: () => void;
}

export default function REITDepositForm({
  holdings,
  accounts,
  preselectedHoldingId,
  onSave,
  onCancel,
}: REITDepositFormProps) {
  const [holdingId, setHoldingId] = useState(preselectedHoldingId ?? "");
  const [sourceAccountId, setSourceAccountId] = useState("");
  const [unitsPurchased, setUnitsPurchased] = useState("");
  const [pricePerUnit, setPricePerUnit] = useState("");
  const [depositCurrency, setDepositCurrency] = useState("");
  const [fee, setFee] = useState("");
  const [depositDate, setDepositDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [notes, setNotes] = useState("");

  const { data: currencyList, isLoading: isLoadingCurrencies } = useCurrencyList();
  const { getRate, isLoading: isLoadingRates } = useExchangeRates();

  // Auto-fill deposit currency from selected holding
  const handleHoldingChange = (id: string) => {
    setHoldingId(id);
    const holding = holdings.find((h) => h.id === id);
    if (holding) {
      setDepositCurrency(holding.currency);
    }
  };

  // On mount, if preselectedHoldingId, set currency from it
  useEffect(() => {
    if (preselectedHoldingId) {
      const holding = holdings.find((h) => h.id === preselectedHoldingId);
      if (holding) setDepositCurrency(holding.currency);
    }
  }, [preselectedHoldingId, holdings]);

  const selectedHolding = holdings.find((h) => h.id === holdingId);
  const selectedAccount = accounts.find((a) => a.id === sourceAccountId);
  const sourceAccountCurrency = selectedAccount?.currency ?? "";

  const parsedUnits = parseFloat(unitsPurchased) || 0;
  const parsedPrice = parseFloat(pricePerUnit) || 0;
  const parsedFee = parseFloat(fee) || 0;
  const total = parsedUnits * parsedPrice;

  const showConversionPreview =
    !!depositCurrency &&
    !!sourceAccountCurrency &&
    depositCurrency !== sourceAccountCurrency &&
    total > 0;

  // Estimate cost in source account currency
  const rate =
    showConversionPreview ? (getRate(depositCurrency, sourceAccountCurrency) ?? 1) : 1;
  const estimatedCost = (total + parsedFee) * rate;
  const hasInsufficientFunds =
    !!selectedAccount && estimatedCost > selectedAccount.balance;

  const disambiguatedAccounts = disambiguateDisplayNames(
    accounts.map((a) => ({ ...a, displayName: getDisplayName(a) }))
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !holdingId ||
      !sourceAccountId ||
      parsedUnits <= 0 ||
      parsedPrice <= 0 ||
      !depositCurrency ||
      !depositDate
    ) {
      return;
    }
    await onSave({
      reitHoldingId: holdingId,
      sourceAccountId: sourceAccountId,
      unitsPurchased: parsedUnits,
      pricePerUnit: parsedPrice,
      depositCurrency: depositCurrency,
      fee: parsedFee || undefined,
      depositDate: depositDate,
      notes: notes.trim() || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" aria-label="Record REIT deposit">
      {/* REIT Holding */}
      <div className="space-y-2">
        <Label htmlFor="deposit-holding">REIT Holding *</Label>
        <Select value={holdingId} onValueChange={handleHoldingChange}>
          <SelectTrigger id="deposit-holding" aria-label="Select REIT holding">
            <SelectValue placeholder="Select a REIT holding" />
          </SelectTrigger>
          <SelectContent>
            {holdings.map((h) => (
              <SelectItem key={h.id} value={h.id}>
                {h.reit_name}
                {h.ticker_symbol ? ` (${h.ticker_symbol})` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Source Account */}
      <div className="space-y-2">
        <Label htmlFor="deposit-source-account">Source Account *</Label>
        <Select value={sourceAccountId} onValueChange={setSourceAccountId}>
          <SelectTrigger id="deposit-source-account" aria-label="Select source account">
            <SelectValue placeholder="Select source account" />
          </SelectTrigger>
          <SelectContent>
            {disambiguatedAccounts.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.displayName} ({a.currency})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedAccount && (
          <p className="text-sm text-muted-foreground">
            {selectedAccount.balance.toFixed(2)} {selectedAccount.currency}
          </p>
        )}
      </div>

      {/* Units & Price */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="deposit-units">Units Purchased *</Label>
          <Input
            id="deposit-units"
            type="number"
            step="0.000001"
            min="0.000001"
            value={unitsPurchased}
            onChange={(e) => setUnitsPurchased(e.target.value)}
            placeholder="0"
            required
            aria-required="true"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="deposit-price">Price per Unit *</Label>
          <Input
            id="deposit-price"
            type="number"
            step="0.0001"
            min="0.0001"
            value={pricePerUnit}
            onChange={(e) => setPricePerUnit(e.target.value)}
            placeholder="0.0000"
            required
            aria-required="true"
          />
        </div>
      </div>

      {/* Deposit Currency */}
      <div className="space-y-2">
        <Label htmlFor="deposit-currency">Deposit Currency *</Label>
        <Select value={depositCurrency} onValueChange={setDepositCurrency}>
          <SelectTrigger id="deposit-currency" aria-label="Select deposit currency">
            <SelectValue
              placeholder={
                isLoadingCurrencies ? "Loading currencies…" : "Select currency"
              }
            />
          </SelectTrigger>
          <SelectContent>
            {(currencyList ?? []).map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Fee */}
      <div className="space-y-2">
        <Label htmlFor="deposit-fee">Fee (optional)</Label>
        <Input
          id="deposit-fee"
          type="number"
          step="0.01"
          min="0"
          value={fee}
          onChange={(e) => setFee(e.target.value)}
          placeholder="0.00"
        />
      </div>

      {/* Computed total */}
      {total > 0 && (
        <div
          className="rounded-md bg-muted px-4 py-3 text-sm"
          role="status"
          aria-live="polite"
        >
          Total:{" "}
          <span className="font-semibold">
            {depositCurrency} {total.toFixed(2)}
          </span>
          {parsedFee > 0 && (
            <span className="text-muted-foreground">
              {" "}+ {depositCurrency} {parsedFee.toFixed(2)} fee
            </span>
          )}
        </div>
      )}

      {/* Conversion preview */}
      {showConversionPreview && (
        <ConversionPreview
          amount={total + parsedFee}
          fromCurrency={depositCurrency}
          toCurrency={sourceAccountCurrency}
          label="Estimated debit"
          getRate={getRate}
          isLoadingRates={isLoadingRates}
        />
      )}

      {/* Insufficient funds warning */}
      {hasInsufficientFunds && (
        <p className="text-sm text-destructive flex items-center gap-1">
          <AlertCircle className="h-3 w-3" aria-hidden="true" />
          Insufficient funds
        </p>
      )}

      {/* Deposit Date */}
      <div className="space-y-2">
        <Label htmlFor="deposit-date">Deposit Date *</Label>
        <Input
          id="deposit-date"
          type="date"
          value={depositDate}
          onChange={(e) => setDepositDate(e.target.value)}
          required
          aria-required="true"
        />
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <Label htmlFor="deposit-notes">Notes</Label>
        <Textarea
          id="deposit-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional notes"
          rows={2}
          aria-label="Optional notes"
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={
            !holdingId ||
            !sourceAccountId ||
            parsedUnits <= 0 ||
            parsedPrice <= 0 ||
            !depositCurrency ||
            !depositDate
          }
        >
          Record Deposit
        </Button>
      </DialogFooter>
    </form>
  );
}
